<template>
    <div class="q-gutter-sm">
        <div v-show="isEventListView">
            <!-- table of events -->
            <q-table
                @request="onRequest"
                :rows="table.data"
                :columns="table.cols"
                v-model:pagination="table.pagination"
                :rows-per-page-options="[table.pagination.rowsPerPage]"
                class="table q-pa-xs"
                :class="$q.screen.gt.md ? 'no-shadow border-grey' : ''"
                table-class="events-table"
                :no-data-label="
                    $t(
                        'There is no downtime event in your provided date range.'
                    )
                "
                :grid="$q.screen.lt.lg"
                row-key="id"
                binary-state-sort
            >
                <!-- top bar -->
                <template v-slot:top>
                    <div class="col-12 q-my-sm q-px-md">
                        <div class="text-h4 text-weight-medium">
                            {{ $t('Downtime Events') }}
                        </div>
                        <div class="page-description">
                            {{
                                $t(
                                    'Track and analyze phases for {facilityName}.',
                                    {
                                        facilityName: facility?.name
                                    }
                                )
                            }}
                        </div>
                    </div>

                    <!-- date time pickers (second row) -->
                    <div class="row full-width q-mb-sm q-col-gutter-sm q-px-sm">
                        <date-time-picker
                            id="start-period-input"
                            :model-value="dateTimePicker.start"
                            :dense="true"
                            :label="$t('Start')"
                            :readonly="true"
                            class="col-lg-3 col-xs-6"
                            @update:model-value="
                                updateDateRange('start', $event)
                            "
                        />

                        <date-time-picker
                            id="end-period-input"
                            :model-value="dateTimePicker.end"
                            :dense="true"
                            :label="$t('End')"
                            :readonly="true"
                            class="col-lg-3 col-xs-6"
                            @update:model-value="updateDateRange('end', $event)"
                        />
                    </div>

                    <!-- filters and buttons (third row)-->
                    <div
                        class="row full-width justify-between q-col-gutter-sm q-px-sm"
                    >
                        <remote-select
                            id="asset-select"
                            :model-value="eventFilter.assets"
                            @update:model-value="eventFilter.assets = $event"
                            :label="$t('Asset')"
                            :baseURL="'assets/'"
                            :urlParams="{
                                facility: facilityId,
                                ordering: 'name'
                            }"
                            class="col-lg-3 col-md-6 col-sm-6 col-xs-12"
                            dense
                            outlined
                            searchable
                            clearable
                            @clear="eventFilter.assets = []"
                            :multiple="true"
                        />
                        <table-select
                            id="product-select"
                            v-model="eventFilter.product"
                            @confirmSelection="
                                eventFilter.product =
                                    productSelectProps.selection
                            "
                            :baseURL="productSelectProps.url"
                            :queryParams="productSelectProps.queryParams"
                            :label="productSelectProps.label"
                            :dialogTitle="productSelectProps.dialogTitle"
                            :searchLabel="productSelectProps.searchLabel"
                            :dense="true"
                            row-key="id"
                            class="col-lg-3 col-md-6 col-sm-6 col-xs-12"
                        >
                            <template v-slot:item="data">
                                <product-card
                                    @select="
                                        select(
                                            data.props.row,
                                            productSelectProps
                                        )
                                    "
                                    :name="data.props.row.name"
                                    :code="data.props.row.code"
                                    :description="data.props.row.description"
                                    :unit-measure="data.props.row.unit_measure"
                                    :unit-packing="data.props.row.unit_packing"
                                    :isSelected="
                                        productSelectProps.selection &&
                                        productSelectProps.selection.value ===
                                            data.props.row.id
                                    "
                                    :isSelectable="true"
                                />
                            </template>
                        </table-select>
                        <table-select
                            id="causes-select"
                            v-model="eventFilter.cause"
                            @confirmSelection="
                                eventFilter.cause = causeSelectProps.selection
                            "
                            :baseURL="causeSelectProps.url"
                            :queryParams="causeSelectProps.queryParams"
                            :label="causeSelectProps.label"
                            :dialogTitle="causeSelectProps.dialogTitle"
                            :searchLabel="causeSelectProps.searchLabel"
                            :dense="true"
                            class="col-lg-3 col-md-6 col-sm-6 col-xs-12"
                        >
                            <template v-slot:item="data">
                                <cause-card
                                    @select="
                                        select(data.props.row, causeSelectProps)
                                    "
                                    :name="data.props.row.name"
                                    :lossType="data.props.row.type_of_loss"
                                    :categoryName="data.props.row.category_name"
                                    :planned="data.props.row.planned"
                                    :description="data.props.row.description"
                                    :targetDowntime="
                                        data.props.row.target_down_time
                                    "
                                    :isSelected="
                                        causeSelectProps.selection &&
                                        causeSelectProps.selection.value ===
                                            data.props.row.id
                                    "
                                    :isSelectable="true"
                                    :equipmentFailure="
                                        data.props.row.equipment_failure
                                    "
                                />
                            </template>
                        </table-select>
                        <div
                            class="col-lg-3 col-md-12 col-sm-12 col-xs-12 text-right"
                        >
                            <q-btn
                                @click="refresh"
                                id="refresh-list-button"
                                :class="{ 'half-width': $q.screen.lt.lg }"
                                :label="$t('Refresh')"
                                icon="fas fa-sync"
                                color="accent"
                            >
                                <q-tooltip>{{ $t('Refresh') }}</q-tooltip>
                            </q-btn>
                            <q-btn
                                @click="showEventPhasesDialog = true"
                                :label="$t('Download')"
                                color="accent"
                                outline
                                class="q-ml-sm"
                                :class="{ 'half-width': $q.screen.lt.lg }"
                            >
                                <q-tooltip>{{
                                    $t('Download Phases')
                                }}</q-tooltip>
                            </q-btn>
                        </div>
                    </div>
                    <div class="q-pa-sm">
                        <q-checkbox
                            v-model="eventFilter.showUnjustifiedOnly"
                            :label="$t('Hide justified events')"
                            color="accent"
                        >
                        </q-checkbox>
                        <q-checkbox
                            v-model="eventFilter.showMajorOnly"
                            :label="$t('Hide micro events')"
                            color="accent"
                        >
                            <span @click.stop class="text-accent">
                                <q-icon
                                    class="q-ml-xs"
                                    style="margin-bottom: 3px"
                                    name="fas fa-question-circle"
                                    size="xs"
                                >
                                    <q-tooltip>
                                        <span>
                                            {{
                                                $t(
                                                    'Micro events are events with duration less than'
                                                )
                                            }}
                                            <span style="font-weight: 900">
                                                {{
                                                    $t(
                                                        'max downtime justification - max downtime delay'
                                                    )
                                                }}
                                            </span>
                                        </span>
                                    </q-tooltip>
                                </q-icon>
                            </span>
                        </q-checkbox>
                    </div>
                </template>

                <!-- Cause column -->
                <template v-slot:body-cell-cause="props">
                    <q-td :props="props">
                        <div v-if="props.row.cause">
                            {{ props.row.causeName }}
                        </div>
                        <div v-else>
                            <div v-if="props.row.isMajor">
                                <!-- Show list of causes -->
                                <missing-cause-label
                                    class="missing-cause-label"
                                    v-model="causeSelectProps.selection"
                                    :phaseId="props.row.id"
                                    :phaseEnd="props.row.end"
                                    :phaseUUID="props.row.uuid"
                                    @refreshList="refresh"
                                    @clearSelection="
                                        causeSelectProps.selection = null
                                    "
                                    :baseURL="`assets/${props.row.asset}/causes/`"
                                    :queryParams="causeSelectProps.queryParams"
                                    :label="causeSelectProps.label"
                                    :dialogTitle="causeSelectProps.dialogTitle"
                                    :searchLabel="causeSelectProps.searchLabel"
                                    row-key="id"
                                    :isDense="true"
                                    :isFlat="true"
                                >
                                    <template v-slot:item="data">
                                        <cause-card
                                            @select="
                                                select(
                                                    data.props.row,
                                                    causeSelectProps
                                                )
                                            "
                                            :name="data.props.row.name"
                                            :lossType="
                                                data.props.row.type_of_loss
                                            "
                                            :categoryName="
                                                data.props.row.category_name
                                            "
                                            :planned="data.props.row.planned"
                                            :description="
                                                data.props.row.description
                                            "
                                            :targetDowntime="
                                                data.props.row.target_down_time
                                            "
                                            :isSelected="
                                                causeSelectProps.selection &&
                                                causeSelectProps.selection
                                                    .value === data.props.row.id
                                            "
                                            :isSelectable="true"
                                            :equipmentFailure="
                                                data.props.row.equipment_failure
                                            "
                                        ></cause-card>
                                    </template>
                                </missing-cause-label>
                            </div>
                        </div>
                    </q-td>
                </template>

                <!-- Status column -->
                <template v-slot:body-cell-status="props">
                    <q-td :props="props">
                        <div>
                            <q-chip
                                class="event-table-status"
                                style="font-weight: 500"
                                outline
                                :color="
                                    ASSET_STATUS_TO_STYLE[props.row.phase][
                                        'color'
                                    ]
                                "
                                :label="
                                    $t(
                                        ASSET_STATUS_TO_STYLE[props.row.phase][
                                            'label'
                                        ]
                                    )
                                "
                                square
                            >
                            </q-chip>
                        </div>
                    </q-td>
                </template>

                <!-- Actions column -->
                <template v-slot:body-cell-actions="props">
                    <q-td :props="props">
                        <div class="row">
                            <!-- "Update event" button -->
                            <q-btn
                                @click="showEventDetail(props.row)"
                                :disable="
                                    !isPhaseEditable(props.row.end) ||
                                    $userCannot('edit', 'phases')
                                "
                                class="action-button"
                                color="accent"
                                icon="fas fa-edit"
                                id="update-event-button"
                                :size="$q.screen.gt.sm ? 'sm' : 'xs'"
                                outline
                            >
                                <q-tooltip
                                    v-if="
                                        getUneditableErrorMessage(props.row.end)
                                    "
                                >
                                    {{
                                        getUneditableErrorMessage(props.row.end)
                                    }}
                                </q-tooltip>
                            </q-btn>
                            <!-- "Split event" button -->
                            <q-btn
                                @click="showSplitDialog(props.row)"
                                id="split-event-button"
                                :disable="$userCannot('split', 'phases')"
                                class="action-button"
                                :size="$q.screen.gt.sm ? 'sm' : 'xs'"
                                :color="
                                    $userCannot('split', 'phases')
                                        ? 'grey'
                                        : 'warning'
                                "
                                outline
                            >
                                <q-icon
                                    style="text-align: center; font-weight: 900"
                                    name="call_split"
                                />
                            </q-btn>
                            <q-btn
                                @click="
                                    $router.push({
                                        name: 'asset_timeline',
                                        params: {
                                            assetId: props.row.asset
                                        },
                                        query: {
                                            datetime: props.row.start
                                        }
                                    })
                                "
                                class="action-button"
                                icon="fas fa-history"
                                color="accent"
                                :size="$q.screen.gt.sm ? 'sm' : 'xs'"
                                no-caps
                                outline
                            >
                                <q-tooltip>
                                    {{
                                        $t(
                                            'Go to the phase timeline of this asset'
                                        )
                                    }}
                                </q-tooltip>
                            </q-btn>
                        </div>
                    </q-td>
                </template>
                <!-- start and end column -->
                <template v-slot:body-cell-start="props">
                    <q-td :props="props">
                        {{ formatDate(props.row.start, userLocale) }}
                    </q-td>
                </template>
                <template v-slot:body-cell-end="props">
                    <q-td :props="props">
                        {{ formatDate(props.row.end, userLocale) }}
                    </q-td>
                </template>
                <!-- Description column -->
                <template v-slot:body-cell-description="props">
                    <q-td :props="props">
                        {{ truncateText(props.row.description) }}
                        <q-tooltip
                            v-if="props.row.description !== ''"
                            max-width="600px"
                            anchor="center left"
                        >
                            {{ props.row.description }}
                        </q-tooltip>
                    </q-td>
                </template>
                <!-- Proximity sensor reading column -->
                <template v-slot:body-cell-proximity_reading="props">
                    <q-td :props="props">
                        <q-icon
                            v-if="props.row.proximity_reading !== null"
                            :name="
                                PROXIMITY_SENSOR_LABELS[
                                    props.row.proximity_reading
                                ]?.list_icon
                            "
                            class="q-mr-xs"
                            size="sm"
                            :color="
                                props.row.proximity_reading === 1
                                    ? 'positive'
                                    : 'negative'
                            "
                        >
                            <q-tooltip anchor="bottom middle" self="top middle">
                                {{
                                    $t(
                                        PROXIMITY_SENSOR_LABELS[
                                            props.row.proximity_reading
                                        ]?.label
                                    )
                                }}
                            </q-tooltip>
                        </q-icon>
                    </q-td>
                </template>
                <!-- Card style for grid mode -->
                <template v-slot:item="props">
                    <div class="full-width q-pa-xs">
                        <event-card
                            @save="getEvents"
                            @refreshList="refresh"
                            :facilityId="facilityId"
                            :assetId="props.row.asset"
                            :eventId="props.row.id"
                            :eventUUID="props.row.uuid"
                            :assetName="props.row.assetName"
                            :phase="props.row.phase"
                            :start="props.row.start"
                            :end="props.row.end === null ? null : props.row.end"
                            :status="props.row.phase"
                            :cause="props.row.cause"
                            :causeName="props.row.causeName"
                            :product="props.row.product"
                            :productName="props.row.productName"
                            :description="props.row.description"
                            :proximityReading="props.row.proximity_reading"
                            :productDuration="
                                convertDurationToDHMS(
                                    calcNoProductDuration(props.row)
                                )
                            "
                            :phaseDuration="
                                convertDurationToDHMS(props.row.duration_s)
                            "
                            :isMajor="props.row.isMajor"
                            @split="showSplitDialog(props.row)"
                        />
                    </div>
                </template>
            </q-table>
        </div>

        <!-- split event dialog -->
        <split-event-dialog
            v-if="splitDialog.show"
            :show="splitDialog.show"
            :event="splitDialog.event"
            @splitSuccess="splitSuccess"
            @close="closeSplitDialog"
        />

        <!-- Detail view -->
        <div v-if="!isEventListView">
            <events-detail
                @return="onEventDetailReturn"
                @save="getEvents"
                :editEvent="true"
                :facilityId="facilityId"
                :asset="selectedEvent.asset"
                :eventId="selectedEvent.eventId"
                :eventUUID="selectedEvent.eventUUID"
                :product="selectedEvent.product"
                :cause="selectedEvent.cause"
                :status="selectedEvent.status"
                :start="selectedEvent.start"
                :end="selectedEvent.end"
                :description="selectedEvent.description"
                :proximityReading="selectedEvent.proximity_reading"
                :causeName="selectedEvent.causeName"
                :productName="selectedEvent.productName"
            />
        </div>
        <!-- Dialog box for export phases starts -->
        <export-phases
            v-if="showEventPhasesDialog"
            :facilityId="`${facility?.id}`"
            :enableDowntimeOnly="true"
            :headerText="$t('Export Phases')"
            :startDate="dateTimePicker.start"
            :endDate="dateTimePicker.end"
            :class="[
                $q.screen.xs ? 'full-width float-right q-mt-sm q-mb-sm' : '',
                $q.screen.lt.lg
                    ? 'half-width float-right'
                    : 'float-right q-mx-sm'
            ]"
            @close="closeEventPhaseDialog"
        />
        <!-- Dialog box for export phases ends -->
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import DateTimePicker from '@components/DateTimePicker.vue';
import RemoteSelect from '@components/RemoteSelect.vue';
import SplitEventDialog from './SplitEventDialog';
import UpdateURL from '@mixins/UpdateURL.vue';
import TableSelect from '@components/TableSelect.vue';
import CauseCard from '@components/CauseCard.vue';
import ProductCard from '@components/ProductCard.vue';
import EventCard from '@components/EventCard.vue';
import ExportPhases from '@components/ExportPhases.vue';
import { getLocaleFormat } from '@models/timeformats.js';
import { convertDurationToDHMS } from '@utils/time.js';
import { ASSET_STATUS_TO_STYLE } from '@models/assets.js';
import { PROXIMITY_SENSOR_LABELS } from '@models/devices.js';
import EventsDetail from './EventDetail.vue';
import MissingCauseLabel from '@components/MissingCauseLabel.vue';
import {
    isPhaseEditable,
    getUneditableErrorMessage
} from '@utils/phaseChecker.js';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';
import { calcNoProductDuration } from '@utils/events.js';
import { useFacilitiesStore } from '@stores/facilities';

export default {
    name: 'EventList',
    components: {
        CauseCard,
        ProductCard,
        DateTimePicker,
        SplitEventDialog,
        RemoteSelect,
        TableSelect,
        EventCard,
        EventsDetail,
        ExportPhases,
        MissingCauseLabel
    },
    mixins: [BreadcrumbsManager, UpdateURL],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    async created() {
        // init time in the datetime picker
        this.initDateTimeRange();
        // set up the breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility?.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility?.id
                    }
                }
            },
            {
                label: this.$t('Downtime Events')
            }
        ]);
        await this.resetPaginationAndRequestEvents();
    },
    data() {
        return {
            // models
            ASSET_STATUS_TO_STYLE,
            PROXIMITY_SENSOR_LABELS,
            baseURL: 'events/',
            url: 'events/',
            events: [],
            // datetime
            dateTimePicker: {
                start: '',
                end: ''
            },
            selected: [],
            isEventListView: true,

            // split event dialog related data
            splitDialog: {
                event: {},
                show: false
            },
            showEventPhasesDialog: false,
            table: {
                data: [],
                cols: [
                    {
                        name: 'status',
                        field: 'phase',
                        label: 'Status',
                        required: true,
                        align: 'left',
                        sortable: 'false'
                    },
                    {
                        name: 'asset',
                        required: true,
                        label: this.$t('Asset'),
                        field: 'assetName',
                        align: 'left',
                        sortable: false
                    },
                    {
                        name: 'product',
                        required: true,
                        label: this.$t('Product'),
                        field: 'productName',
                        align: 'left',
                        sortable: false
                    },
                    {
                        name: 'cause',
                        required: true,
                        label: this.$t('Cause'),
                        field: 'causeName',
                        align: 'left',
                        sortable: false
                    },
                    {
                        name: 'start',
                        label: this.$t('Start Time'),
                        field: row => row.start,
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'end',
                        label: this.$t('End Time'),
                        field: row => row.end,
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'noProductDuration',
                        field: 'noProductDuration',
                        label: this.$t('No Product Duration'),
                        align: 'right',
                        format: (val, row) => {
                            const noProductDuration =
                                this.calcNoProductDuration(row);
                            return convertDurationToDHMS(noProductDuration);
                        }
                    },
                    {
                        name: 'duration_s',
                        field: 'duration_s',
                        label: this.$t('Phase Duration'),
                        align: 'right',
                        sortable: true,
                        format(val) {
                            // duration value is in seconds
                            return convertDurationToDHMS(val);
                        }
                    },
                    {
                        name: 'description',
                        label: this.$t('Description'),
                        field: row => row.description,
                        align: 'left'
                    },
                    {
                        name: 'proximity_reading',
                        label: this.$t('Product on line?'),
                        field: row => row.proximity_reading,
                        align: 'center'
                    },
                    {
                        name: 'actions',
                        label: this.$t('Actions'),
                        field: 'actions',
                        align: 'left'
                    }
                ],
                pagination: {
                    sortBy: 'start',
                    descending: true,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                }
            },
            causeListManager: {
                options: [],
                isDisabled: true,
                isFetching: false,
                nextPage: null,
                isLoading: false,
                totalCount: null
            },
            eventFilter: {
                assets: [],
                product: null,
                cause: null,
                showMajorOnly: true,
                showUnjustifiedOnly: false
            },

            gridButtonSize: 'lg',

            // selectors
            causeSelectProps: {
                url: '/causes/',
                queryParams: {
                    facility: this.facilityId,
                    ordering: 'name'
                },
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null
            },
            productSelectProps: {
                url: '/products/',
                queryParams: {
                    facility: this.facilityId,
                    ordering: 'name'
                },
                label: this.$t('Products'),
                dialogTitle: this.$t('Select a product'),
                searchLabel: this.$t('Search product'),
                selection: null
            },
            // phase selected for edit or splitting
            selectedEvent: {
                eventId: null,
                eventUUID: null,
                product: null,
                cause: null,
                asset: null,
                start: null,
                status: null,
                end: null,
                description: null,
                proximity_reading: null,
                causeName: null,
                productName: null
            }
        };
    },
    watch: {
        eventFilter: {
            deep: true,
            immediate: true,
            async handler(newVal) {
                // send request to server
                if (
                    !(
                        this.dateTimePicker.start === '' &&
                        this.dateTimePicker.end === ''
                    )
                ) {
                    this.resetPaginationAndRequestEvents();
                }
            }
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        ...mapState(useFacilitiesStore, ['getFacility']),
        /**
         * Get selected facility.
         * @return {Object} - facility.
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Get asset url with facility id.
         * @return {String} asset url
         */
        assetUrl() {
            return `assets/?facility=${this.facilityId}`;
        },
        /**
         * Get product url with facility id.
         * @return {String} product url
         */
        productUrl() {
            return `products/?facility=${this.facilityId}`;
        },
        /**
         * Get cause url with facility id.
         * @return {String} cause url
         */
        causeUrl() {
            return `causes/?facility=${this.facilityId}`;
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        isPhaseEditable,
        getUneditableErrorMessage,
        calcNoProductDuration,
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @param {String} locale
         * @return {String} datetime in given locale.
         */
        formatDate(datetime, locale) {
            return getLocaleFormat(datetime, locale);
        },
        /**
         * Truncate the text when the length is larger than 30
         * @return {String} the text after truncation
         */
        truncateText(text) {
            if (text && text.length >= 20) {
                return text.slice(0, 20) + '...';
            } else {
                return text;
            }
        },
        convertDurationToDHMS,
        /**
         * Fetch event data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         * @param {Object} props.pagination
         */
        async onRequest(props) {
            // update pagination object
            this.table.pagination = props.pagination;
            // get current pagination state attributes
            const { descending, page, sortBy } = props.pagination;

            let urlParams = {
                facility: this.facilityId,
                ordering: descending ? `-${sortBy}` : `${sortBy}`,
                page: page,
                start__lte: this.dateTimePicker.end,
                end__gte: this.dateTimePicker.start
            };
            if (this.eventFilter.assets !== null) {
                if (this.eventFilter.assets.length > 0) {
                    urlParams['asset__in'] = this.eventFilter.assets.map(
                        asset => asset.value
                    );
                }
            }
            if (this.eventFilter.product !== null) {
                urlParams['product'] = this.eventFilter.product.value;
            }
            if (this.eventFilter.cause !== null) {
                urlParams['cause'] = this.eventFilter.cause.value;
            }
            // if showMajorOnly is false, do not add query is_major=false, as this will show all micro events only, instead of both micro/major events.
            if (this.eventFilter.showMajorOnly) {
                urlParams['is_major'] = true;
            }
            // if showUjustifiedOnly is false, do not add has_cause=true, as this will filter for only those events with causes, instead of events with or without cause.
            if (this.eventFilter.showUnjustifiedOnly) {
                urlParams['has_cause'] = false;
            }

            // update URL
            this.url = this.updateURL({
                baseURL: this.baseURL,
                params: urlParams
            });
            // clear existing data
            this.events = [];
            this.table.data = [];

            // fetch events from the server
            await this.getEvents();
        },
        /**
         * Get events from the server.
         */
        async getEvents() {
            // begin loading
            this.beginLoading(`${this.$t('Loading events')}...`);

            const errorMessage = this.$t(
                'Failed to load events. Please try again later'
            );
            try {
                // send GET HTTPS request
                let response = await this.$axios.get(this.url);
                if (response.status === 200) {
                    // clear existing data
                    this.table.data = [];
                    // get raw event response
                    const rawEvents = response.data.results;
                    // format event data
                    rawEvents.forEach(rawEvent => {
                        const event = {
                            id: rawEvent['id'],
                            uuid: rawEvent['uuid'],
                            facility: rawEvent['facility'],
                            assetName: rawEvent['asset_name'],
                            asset: rawEvent['asset'],
                            causeName: rawEvent['cause_name'],
                            cause: rawEvent['cause'],
                            productName: rawEvent['product_name'],
                            product: rawEvent['product'],
                            start: rawEvent['start'],
                            end: rawEvent['end'],
                            description: rawEvent['description'],
                            proximity_reading: rawEvent['proximity_reading'],
                            duration_s: rawEvent['duration_s'],
                            phase: rawEvent['phase'],
                            isMajor: rawEvent['is_major'],
                            max_downtime_delay: rawEvent['max_downtime_delay'],
                            max_downtime_justification:
                                rawEvent['max_downtime_justification']
                        };
                        // ignore invalid events
                        if (event['duration_s'] >= 0) {
                            this.table.data.push(event);
                        }
                    });
                    // update total events count
                    this.table.pagination.rowsNumber = response.data.count;
                } else {
                    this.$notifyError(errorMessage);
                }
            } catch (error) {
                console.error(error);
                this.$notifyError(errorMessage, error);
            } finally {
                // stop loading
                this.stopLoading();
            }
        },
        /**
         * Refresh events and update date range.
         */
        async refresh() {
            this.initDateTimeRange();
            this.resetPaginationAndRequestEvents();
        },
        /**
         * Update downtime event.
         * @param {Object} row - Event from table row.
         */
        update(row) {
            // route to user update page
            // and pass parameters
            this.$router.push({
                name: 'event_update',
                params: {
                    eventId: row.id,
                    product: row.product,
                    cause: row.cause,
                    asset: row.asset,
                    start: row.start,
                    end: row.end,
                    description: row.description,
                    proximityReading: row.proximity_reading,
                    causeName: row.causeName,
                    productName: row.productName
                }
            });
        },

        /**************
         * Event split methods *
         *************/
        /**
         * Show the split event dialog.
         * @param {Object} row - Event from table row.
         */
        showSplitDialog(row) {
            // init split event dialog
            this.splitDialog.event = row;
            this.splitDialog.show = true;
        },
        /**
         * Close the split event diaglog.
         * Triggered when the user clicks Cancel button in the split dialog.
         */
        closeSplitDialog() {
            // close the dialog
            this.splitDialog.event = {};
            this.splitDialog.show = false;
        },
        splitSuccess() {
            this.closeSplitDialog();
            this.getEvents();
        },
        /**************
         * Filter methods *
         *************/
        /**
         * Updates the date range based on the provided type and date.
         * @param {string} type - The type of date to update ('start' or 'end').
         * @param {Date} date - The new date value to set.
         */
        updateDateRange(type, date) {
            if (!date || (type !== 'start' && type !== 'end')) {
                return;
            }

            // Don't update if the date is the same
            if (this.dateTimePicker[type] === date) {
                return;
            }

            this.dateTimePicker[type] = date;

            if (
                this.dateTimePicker.start &&
                this.dateTimePicker.end &&
                Date.parse(this.dateTimePicker.start) >
                    Date.parse(this.dateTimePicker.end)
            ) {
                this.$notifyError(
                    this.$t('Start date must be before end date')
                );
            }

            this.resetPaginationAndRequestEvents();
        },
        /**
         * Reset pagination and request events.
         */
        async resetPaginationAndRequestEvents() {
            if (
                !(
                    this.dateTimePicker.start === '' &&
                    this.dateTimePicker.end === ''
                )
            ) {
                // send query with first page
                this.table.pagination['page'] = 1;
                // send request to server
                await this.onRequest({
                    pagination: this.table.pagination
                });
            }
        },
        /**************
         * Event Detail methods *
         *************/
        /**
         * Show the split event dialog.
         * @param {Object} row - Event from table row.
         */
        showEventDetail(row) {
            // init split event dialog
            this.selectedEvent = {
                eventId: row.id,
                eventUUID: row.uuid,
                product: row.product,
                cause: row.cause,
                asset: row.asset,
                start: row.start,
                status: row.phase,
                end: row.end,
                description: row.description,
                proximity_reading: row.proximity_reading,
                causeName: row.causeName,
                productName: row.productName
            };
            this.isEventListView = false;
        },
        clearSelectedEvent() {
            this.selectedEvent = {
                eventId: null,
                eventUUID: null,
                product: null,
                cause: null,
                status: null,
                asset: null,
                start: null,
                end: null,
                description: null,
                proximity_reading: null,
                causeName: null,
                productName: null
            };
        },

        async onEventDetailReturn() {
            this.isEventListView = true;
            this.clearSelectedEvent();
            await this.getEvents();
        },
        /**************
         * Datetime related methods *
         *************/

        /**
         * init the time range in ISO 8601 format
         * i.e. start: "2020-03-03T20:35:39.700Z"; end: '2020-03-03T21:35:39.700Z'
         */
        initDateTimeRange() {
            const now = new Date();
            const millisecondsInWeek = 7 * 24 * 60 * 60 * 1000; // More readable calculation
            const oneWeekAgo = new Date(now.getTime() - millisecondsInWeek);

            // Set milliseconds to 000 to have a consistent format
            // This is necessary because the date time picker sets the milliseconds to 000
            // and we need to compare the initial date to the updated date in the updateDateRange method
            // to avoid repeated requests
            const formatDateForPicker = date => {
                const d = new Date(date);
                d.setMilliseconds(0);
                return d.toISOString();
            };

            this.dateTimePicker = {
                start: formatDateForPicker(oneWeekAgo),
                end: formatDateForPicker(now)
            };
        },
        closeEventPhaseDialog() {
            this.showEventPhasesDialog = !this.showEventPhasesDialog;
        },
        /**
         * Select/ deselect cause and products in table-select component
         * @param {Object} row - Event from table row.
         * @param {Object} props - Table selection props (causeTableSelect, productTableSelect, MissingCauseLabel)
         */
        select(row, props) {
            if (props.selection && props.selection.value === row.id) {
                props.selection = null;
            } else {
                props.selection = {
                    label: row.name,
                    value: row.id
                };
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.table {
    height: 100%;
}

// align buttons to the right end of the page
.buttons {
    padding-right: 0px;
}

.action-button {
    padding: 12px;
    font-size: 12px;
    margin: 5px;
}

.missing-cause {
    color: $negative;
    font-weight: 500;
}

.events-table {
    height: 80vh;
}

// remove table top padding
:deep(.q-table__top) {
    padding: 0px;
}

// remove q-input bottom padding due to table-select validator
:deep(.q-field--with-bottom) {
    padding: 0px;
}

:deep(.events-table) {
    // table to fill entire panel
    height: 618px;
    max-height: none;
}

:deep(.events-table-cell) {
    font-size: 14px;
}

:deep(.q-table__container > div:first-child) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
}

.events-table-actions-col {
    width: 100px;
}

.events-table-button :deep(.q-btn__wrapper) {
    padding: 6px;
    font-size: 12px;
}

.events-table-status {
    font-size: 13px;
    padding: 4px;
}
.half-width {
    width: calc(50% - 5px);
}
</style>
